import axios from 'axios'
import { classifyGatewayError } from './helpers'

// Dedicated axios instance for the voice gateway, same approach as
// lib/longdo.js. lib/fetch.js sets X-Requested-With on the global axios
// defaults, and the gateway's CORS allow-list (Authorization, Content-Type)
// does not include it, so any cross-origin call would fail its preflight.
// axios.create() copies the defaults once, so deleting the header here is
// enough even if lib/fetch.js runs later.
const gatewayHttp = axios.create({ timeout: 10000 })
if (gatewayHttp.defaults.headers && gatewayHttp.defaults.headers.common) {
  delete gatewayHttp.defaults.headers.common['X-Requested-With']
}

const authHeaders = token => ({ Authorization: `Bearer ${token}` })

// Every call resolves to { ok, data } or { ok: false, kind, status }, where
// kind comes from classifyGatewayError. Nothing throws into the saga.
function failure(err) {
  return {
    ok: false,
    kind: classifyGatewayError(err),
    status: err && err.response ? err.response.status : null,
  }
}

// SIP credentials for this tab. The gateway derives ext and password from the
// verified token and the session id, and returns the realm FreeSWITCH checks
// the digest against, so none of them are guessed here.
export async function fetchSipCredentials(baseUrl, token, sessionId) {
  try {
    const res = await gatewayHttp.get(`${baseUrl}/sip-credentials`, {
      params: { session: sessionId },
      headers: authHeaders(token),
    })
    const { ext, password, realm } = res.data || {}
    if (!ext || !password || !realm) {
      // Registering with half an answer fails with a bare 403 that logs
      // nothing anywhere; treat it as a gateway fault and retry later.
      return { ok: false, kind: 'unavailable', status: res.status }
    }
    return { ok: true, data: { ext, password, realm } }
  } catch (err) {
    return failure(err)
  }
}

// Presence snapshot of the user's voice-capable vehicles.
export async function fetchVehicles(baseUrl, token) {
  try {
    const res = await gatewayHttp.get(`${baseUrl}/vehicles`, {
      headers: authHeaders(token),
    })
    return { ok: true, data: Array.isArray(res.data) ? res.data : [] }
  } catch (err) {
    return failure(err)
  }
}

// Thin wrapper over the browser WebSocket for /ws/user. It never reconnects
// by itself: the saga decides whether to retry, so a rejected token cannot
// turn into a reconnect loop. Events: open | message | close.
export class GatewaySocket {
  constructor(wsBaseUrl, token) {
    // The token must go in the query string: browsers cannot set headers on
    // a WebSocket. Never log this URL.
    this.url = `${wsBaseUrl}/ws/user?token=${encodeURIComponent(token)}`
    this.ws = null
    this.listener = null
    this.opened = false
  }

  setListener(listener) {
    this.listener = listener
  }

  emit(event) {
    if (this.listener) this.listener(event)
  }

  connect() {
    let ws
    try {
      ws = new WebSocket(this.url)
    } catch (err) {
      // SyntaxError (bad URL) or SecurityError (e.g. ws: from an https page).
      // The same URL will fail again, so this is reported as fatal.
      this.emit({ type: 'close', code: null, fatal: true, wasOpen: false })
      return
    }
    this.ws = ws
    ws.onopen = () => {
      this.opened = true
      this.emit({ type: 'open' })
    }
    ws.onmessage = ev => {
      let msg
      try {
        msg = JSON.parse(ev.data)
      } catch (err) {
        console.warn('[voice] ignoring a malformed gateway frame')
        return
      }
      this.emit({ type: 'message', data: msg })
    }
    // onclose always follows onerror, so only onclose is reported.
    ws.onerror = () => {}
    ws.onclose = ev => {
      this.detach()
      this.emit({
        type: 'close',
        code: ev ? ev.code : null,
        fatal: false,
        wasOpen: this.opened,
      })
    }
  }

  detach() {
    if (!this.ws) return
    this.ws.onopen = null
    this.ws.onmessage = null
    this.ws.onerror = null
    this.ws.onclose = null
  }

  close() {
    this.listener = null
    if (!this.ws) return
    this.detach()
    try {
      this.ws.close()
    } catch (err) {}
    this.ws = null
  }
}
